import React from 'react';
import { Formik } from 'formik';
import {
    TextField,
    withStyles,
    Select,
    MenuItem,
    FormControl,
    Button,
    InputLabel,
    CircularProgress,
    FormHelperText,
} from '@material-ui/core';
import * as Yup from 'yup';
import { DateTimePicker } from '@material-ui/pickers';
import moment from 'moment';
import {getDateFormat} from "../../sc-utilities/date";

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2)
    },
    rootEdit: {
        flexGrow: 1,
        paddingTop: theme.spacing(2)
    },
    submit: {
        width: '120px',
        marginTop: '10px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    cancel: {
        width: '120px',
        marginTop: '10px',
        marginRight: '10px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    next: {
        width: '120px',
        marginTop: '10px',
        marginRight: '10px'
    },
    formControl: {
        width: '100%'
    },

    datePicker: {
        width: '100%'
    }
});

const wipeStatuses = [
    { value: 'noterased', displayValue: 'Not erased' },
    { value: 'erased', displayValue: 'Erased' },
    { value: 'error', displayValue: 'Error' },
    { value: 'warning', displayValue: 'Warning' },
    { value: 'inprogress', displayValue: 'In progress' },
    { value: 'aborted', displayValue: 'Aborted by user' },
    { value: 'started', displayValue: 'Started' }
];

const verificationStatuses = [
    { value: 'notverified', displayValue: 'Not verified' },
    { value: 'verified', displayValue: 'Verified' },
    { value: 'error', displayValue: 'Error' },
    { value: 'warning', displayValue: 'Warning' },
    { value: 'inprogress', displayValue: 'In progress' },
    { value: 'aborted', displayValue: 'Aborted by user' },
    { value: 'started', displayValue: 'Started' }
];

const StorageForm = props => {
    const {
        classes,
        formSettings,
        create,
        initialValues,
        handleSubmit,
        methods,
        translations,
        reportInformation,
        loading,
        edit,
        activeStep,
        securazeSolutions
    } = props;
    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                await handleSubmit(values);
            }}
            validationSchema={Yup.object().shape(formSettings.Validation)}
        >
            {formikProps => {
                const { values, touched, errors, isValid, dirty, isSubmitting, handleChange, handleBlur, handleSubmit, setFieldValue } = formikProps;

                let checkForWipeDate = (!values.WipeFinished || !values.WipeStarted) || (values.WipeFinished && values.WipeStarted && moment(values.WipeFinished).isSameOrAfter(values.WipeStarted));
                let checkForVerificationDate = (!values.VerificationFinished || !values.VerificationStarted) || (values.VerificationFinished && values.VerificationStarted && moment(values.VerificationFinished).isSameOrAfter(values.VerificationStarted));

                const calculateDate = (startedField, finishedField) => {
                    if (values[startedField] && values[finishedField]) {
                        if (moment(values[finishedField]).isSameOrAfter(values[startedField])) {
                            let duration = moment.duration(moment(values[finishedField]).diff(moment(values[startedField])));
                            if (duration < 86400000) {
                                return moment.utc(duration.as('milliseconds')).format('HH:mm:ss')
                            }
                            return Math.floor(duration / 86400000) + ` ${duration >= (86400000 * 2) ? 'days and' : 'day and'} ` + moment.utc(duration.as('milliseconds')).format(`HH:mm:ss`)
                        }
                        return translations['starting_date_cannot'] || 'The starting date cannot be greater than finished day'
                    }
                    else {
                        return '-';
                    }
                }

                return (
                    <div className={classes.root}>
                        <form onSubmit={handleSubmit}>
                            {activeStep === 0 ?
                                <>
                                    <TextField
                                        name="Type"
                                        disabled={true}
                                        value={values.Type}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.Type && touched.Type)}
                                        placeholder={translations['type'] || 'Type'}
                                        label={translations['type'] || 'Type'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    />
                                    <TextField
                                        name="Transport"
                                        value={values.Transport}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.Transport && touched.Transport)}
                                        placeholder={translations['report_transport'] || 'Transport'}
                                        label={translations['report_transport'] || 'Transport'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    />
                                    <TextField
                                        name="Size"
                                        value={values.Size}
                                        type='number'
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.Size && touched.Size)}
                                        placeholder={translations['size'] || 'Size'}
                                        label={translations['size'] || 'Size'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    />
                                    <TextField
                                        name="Vendor"
                                        value={values.Vendor}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.Vendor && touched.Vendor)}
                                        placeholder={translations['vendor'] || 'Vendor'}
                                        label={translations['vendor'] || 'Vendor'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    />
                                    <TextField
                                        name="ComponentModel"
                                        value={values.ComponentModel}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.ComponentModel && touched.ComponentModel)}
                                        placeholder={translations['model'] || 'Model'}
                                        label={translations['model'] || 'Model'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    />
                                    <TextField
                                        name="SerialNumber"
                                        value={values.SerialNumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.SerialNumber && touched.SerialNumber)}
                                        placeholder={translations['serial_number'] || 'Serial number'}
                                        label={translations['serial_number'] || 'Serial number'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    />
                                    <TextField
                                        name="FirmwareVersion"
                                        value={values.FirmwareVersion}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.FirmwareVersion && touched.FirmwareVersion)}
                                        placeholder={translations['firmware_version'] || 'Firmware version'}
                                        label={translations['firmware_version'] || 'Firmware version'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    />
                                    <TextField
                                        name="LogicalName"
                                        value={values.LogicalName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.LogicalName && touched.LogicalName)}
                                        placeholder={translations['logical_name'] || 'Logical name'}
                                        label={translations['logical_name'] || 'Logical name'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    />
                                    <TextField
                                        name="Description"
                                        value={values.Description}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        multiline
                                        error={!!(errors.Description && touched.Description)}
                                        placeholder={translations['description'] || 'Description'}
                                        label={translations['description'] || 'Description'}
                                        margin={'dense'}
                                        fullWidth={true}
                                    ></TextField>

                                </> :
                                activeStep === 1 ?
                                    <>
                                        <TextField
                                            name="Sectors"
                                            value={values.Sectors}
                                            onChange={handleChange}
                                            type={'number'}
                                            onBlur={handleBlur}
                                            error={!!(errors.Sectors && touched.Sectors)}
                                            placeholder={translations['sectors'] || 'Sectors'}
                                            label={translations['sectors'] || 'Sectors'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="ReallocatedSectors"
                                            value={values.ReallocatedSectors}
                                            onChange={handleChange}
                                            type={'number'}
                                            onBlur={handleBlur}
                                            error={!!(errors.ReallocatedSectors && touched.ReallocatedSectors)}
                                            placeholder={translations['relocated_sectors'] || 'Relocated sectors'}
                                            label={translations['relocated_sectors'] || 'Relocated sectors'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="HPA"
                                            value={values.HPA}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.HPA && touched.HPA)}
                                            placeholder={translations['hpa'] || 'HPA'}
                                            label={translations['hpa'] || 'HPA'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            multiline
                                        />
                                        <TextField
                                            name="DCO"
                                            value={values.DCO}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.DCO && touched.DCO)}
                                            placeholder={translations['dco'] || 'DCO'}
                                            label={translations['dco'] || 'DCO'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            multiline
                                        />
                                        <TextField
                                            name="OverallState"
                                            value={values.OverallState}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.OverallState && touched.OverallState)}
                                            placeholder={translations['overall_state'] || 'Overall state'}
                                            label={translations['overall_state'] || 'Overall state'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            multiline
                                        />
                                        <TextField
                                            name="SmartScore"
                                            value={values.SmartScore}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            type='number'
                                            error={!!(errors.SmartScore && touched.SmartScore)}
                                            placeholder={translations['health'] || 'Health'}
                                            label={translations['health'] || 'Health'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="PowerOnHours"
                                            value={values.PowerOnHours}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            type='number'
                                            error={!!(errors.PowerOnHours && touched.PowerOnHours)}
                                            placeholder={translations['power_on_hours'] || 'Power on hours'}
                                            label={translations['power_on_hours'] || 'Power on hours'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="ProgramFails"
                                            value={values.ProgramFails}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            type='number'
                                            error={!!(errors.ProgramFails && touched.ProgramFails)}
                                            placeholder={translations['write_errors'] || 'Write errors'}
                                            label={translations['write_errors'] || 'Write errors'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            multiline
                                        />
                                        <TextField
                                            name="UncorrectableErrors"
                                            value={values.UncorrectableErrors}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            type='number'
                                            error={!!(errors.UncorrectableErrors && touched.UncorrectableErrors)}
                                            placeholder={translations['permanent_errors'] || 'Permanent errors'}
                                            label={translations['permanent_errors'] || 'Permanent errors'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            multiline
                                        />
                                        <TextField
                                            name="PendingSectors"
                                            value={values.PendingSectors}
                                            onChange={handleChange}
                                            type='number'
                                            onBlur={handleBlur}
                                            error={!!(errors.PendingSectors && touched.PendingSectors)}
                                            placeholder={translations['pending_sectors'] || 'Pending sectors'}
                                            label={translations['pending_sectors'] || 'Pending sectors'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="SMART"
                                            value={values.SMART}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.SMART && touched.SMART)}
                                            placeholder={translations['smart'] || 'SMART'}
                                            label={translations['smart'] || 'SMART'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            multiline
                                        />
                                        <TextField
                                            name="TransportChannelNumber"
                                            value={values.TransportChannelNumber}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.TransportChannelNumber && touched.TransportChannelNumber)}
                                            placeholder={translations['transport_channel_number'] || 'Transport channel number'}
                                            label={translations['transport_channel_number'] || 'Transport channel number'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="BlockSize"
                                            value={values.BlockSize}
                                            type='number'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.BlockSize && touched.BlockSize)}
                                            placeholder={translations['block_size'] || 'Block size'}
                                            label={translations['block_size'] || 'Block size'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="PhysicalBlockSize"
                                            value={values.PhysicalBlockSize}
                                            type='number'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.PhysicalBlockSize && touched.PhysicalBlockSize)}
                                            placeholder={translations['physical_block_size'] || 'Physical block size'}
                                            label={translations['physical_block_size'] || 'Physical block size'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="LogicalBlockSize"
                                            value={values.LogicalBlockSize}
                                            type='number'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.LogicalBlockSize && touched.LogicalBlockSize)}
                                            placeholder={translations['logical_block_size'] || 'Logical block size'}
                                            label={translations['logical_block_size'] || 'Logical block size'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="TransferMode"
                                            value={values.TransferMode}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.TransferMode && touched.TransferMode)}
                                            placeholder={translations['transfer_mode'] || 'Transfer mode'}
                                            label={translations['transfer_mode'] || 'Transfer mode'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="AMA"
                                            value={values.AMA}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.AMA && touched.AMA)}
                                            placeholder={translations['ama'] || 'AMA'}
                                            label={translations['ama'] || 'AMA'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="SecurityStatus"
                                            value={values.SecurityStatus}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.SecurityStatus && touched.SecurityStatus)}
                                            placeholder={translations['security_status'] || 'Security status'}
                                            label={translations['security_status'] || 'Security status'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <FormControl className={classes.formControl} margin={"dense"}>
                                            <InputLabel shrink>{translations['enhanced_security_erase'] || 'Enhanced security erase'}</InputLabel>
                                            <Select
                                                name="EnhancedSecurityErase"
                                                label={translations['enhanced_security_erase'] || 'Enhanced security erase'}
                                                margin={"dense"}
                                                value={values.EnhancedSecurityErase === null || values.EnhancedSecurityErase === undefined ? '' : values.EnhancedSecurityErase}
                                                onChange={(event) => setFieldValue('EnhancedSecurityErase', event.target.value)}
                                                onBlur={handleBlur}
                                                error={!!(errors.EnhancedSecurityErase && touched.EnhancedSecurityErase)}
                                            >
                                                <MenuItem value={true}>{translations['yes'] || 'Yes'}</MenuItem>
                                                <MenuItem value={false}>{translations['no'] || 'No'}</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            name="LifeRemainingPercent"
                                            value={values.LifeRemainingPercent}
                                            type='number'
                                            InputProps={{ inputProps: { min: 0, max: 100 } }}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.LifeRemainingPercent && touched.LifeRemainingPercent)}
                                            placeholder={translations['life_remaining_percent'] || 'Life remaining percent'}
                                            label={translations['life_remaining_percent'] || 'Life remaining percent'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="GrownDefectListCount"
                                            value={values.GrownDefectListCount}
                                            type='number'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.GrownDefectListCount && touched.GrownDefectListCount)}
                                            placeholder={translations['grown_defect_list_count'] || 'Grown defect list count'}
                                            label={translations['grown_defect_list_count'] || 'Grown defect list count'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="PrimaryDefectListCount"
                                            value={values.PrimaryDefectListCount}
                                            type='number'
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.PrimaryDefectListCount && touched.PrimaryDefectListCount)}
                                            placeholder={translations['primary_defect_list_count'] || 'Primary defect list count'}
                                            label={translations['primary_defect_list_count'] || 'Primary defect list count'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <FormControl className={classes.formControl} margin={"dense"}>
                                            <InputLabel shrink>{translations['is_counterfeit'] || 'Is counterfeit'}</InputLabel>
                                            <Select
                                                name="IsCounterfeit"
                                                label={translations['is_counterfeit'] || 'Is counterfeit'}
                                                margin={"dense"}
                                                value={values.IsCounterfeit === null || values.IsCounterfeit === undefined ? '' : values.IsCounterfeit}
                                                onChange={(event) => setFieldValue('IsCounterfeit', event.target.value)}
                                                onBlur={handleBlur}
                                                error={!!(errors.IsCounterfeit && touched.IsCounterfeit)}
                                            >
                                                <MenuItem value={true}>{translations['yes'] || 'Yes'}</MenuItem>
                                                <MenuItem value={false}>{translations['no'] || 'No'}</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            name="CounterfeitReason"
                                            value={values.CounterfeitReason}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.CounterfeitReason && touched.CounterfeitReason)}
                                            placeholder={translations['counterfeit_reason'] || 'Counterfeit reason'}
                                            label={translations['counterfeit_reason'] || 'Counterfeit reason'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="HeliumLevelPercent"
                                            value={values.HeliumLevelPercent}
                                            type='number'
                                            InputProps={{ inputProps: { min: 0, max: 100 } }}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.HeliumLevelPercent && touched.HeliumLevelPercent)}
                                            placeholder={translations['helium_level_percent'] || 'Helium level percent'}
                                            label={translations['helium_level_percent'] || 'Helium level percent'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                    </> :
                                activeStep === 2 ?
                                    <>
                                        <TextField
                                            name="WipeParams"
                                            value={values.WipeParams}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.WipeParams && touched.WipeParams)}
                                            placeholder={translations['wipe_params'] || 'Erasure params'}
                                            label={translations['wipe_params'] || 'Erasure params'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            multiline
                                        />
                                        <TextField
                                            name="WipeResult"
                                            value={values.WipeResult}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.WipeResult && touched.WipeResult)}
                                            placeholder={translations['erasure_result'] || 'Erasure result'}
                                            label={translations['erasure_result'] || 'Erasure result'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            multiline
                                        />
                                        <TextField
                                            name="WipeVerify"
                                            value={values.WipeVerify}
                                            onChange={handleChange}
                                            multiline
                                            onBlur={handleBlur}
                                            error={!!(errors.WipeVerify && touched.WipeVerify)}
                                            placeholder={translations['erasure_verify'] || 'Erasure verify'}
                                            label={translations['erasure_verify'] || 'Erasure verify'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <TextField
                                            name="WipeProgress"
                                            value={values.WipeProgress}
                                            onChange={handleChange}
                                            InputProps={{ inputProps: { min: 0, max: 100 } }}
                                            onBlur={handleBlur}
                                            error={!!(errors.WipeProgress && touched.WipeProgress)}
                                            placeholder={translations['erasure_progress'] || 'Erasure progress'}
                                            label={translations['erasure_progress'] || 'Erasure progress'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            type='number'
                                        />
                                        <FormHelperText>{translations['wipe_started_text'] || 'Please select erasure started date'}</FormHelperText>
                                        <DateTimePicker
                                            clearable
                                            name={'WipeStarted'}
                                            onBlur={handleBlur}
                                            error={!!(errors.WipeStarted && touched.WipeStarted)}
                                            onChange={(date) =>
                                                setFieldValue("WipeStarted", date)
                                            }
                                            ampm={false}
                                            maxDate={values.WipeFinished ? values.WipeFinished : ''}
                                            value={values.WipeStarted !== "" ? values.WipeStarted : null}
                                            margin={"dense"}
                                            onAccept={date => {
                                                setFieldValue("WipeStarted", date)
                                            }}
                                            fullWidth={true}
                                            format={`${getDateFormat()} HH:mm:ss`}
                                        />
                                        <FormHelperText>{translations['wipe_finished_text'] || 'Please select erasure finished date'}</FormHelperText>
                                        <DateTimePicker
                                            clearable
                                            name={'WipeFinished'}
                                            onBlur={handleBlur}
                                            minDate={values.WipeStarted ? values.WipeStarted : moment()}
                                            ampm={false}
                                            error={!!(errors.WipeFinished && touched.WipeFinished)}
                                            onChange={(date) =>
                                                setFieldValue("WipeFinished", date)
                                            }
                                            value={values.WipeFinished !== "" ? values.WipeFinished : null}
                                            margin={"dense"}
                                            onAccept={date => {
                                                setFieldValue("WipeFinished", date)
                                            }}
                                            fullWidth={true}
                                            format={`${getDateFormat()} HH:mm:ss`}
                                        />
                                        <TextField
                                            name="WipeDuration"
                                            value={calculateDate('WipeStarted', 'WipeFinished')}
                                            onChange={handleChange}
                                            multiline
                                            disabled={true}
                                            onBlur={handleBlur}
                                            error={!!(errors.WipeDuration && touched.WipeDuration)}
                                            placeholder={translations['duration'] || 'Duration'}
                                            label={translations['duration'] || 'Duration'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <FormControl className={classes.formControl} margin={"dense"}>
                                            <InputLabel shrink>{translations['wipe_status_text'] || "Please select erasure status"}</InputLabel>
                                            <Select
                                                name="WipeStatus"
                                                label={translations['wipe_status'] || 'Erasure status'}
                                                margin={"dense"}
                                                value={values.WipeStatus}
                                                onChange={handleChange}
                                                error={!!(errors.WipeStatus && touched.WipeStatus)}
                                            >
                                                {(wipeStatuses ? [...wipeStatuses] : []).map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.displayValue}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl className={classes.formControl} margin={"dense"}>
                                            <InputLabel shrink>{translations['wipe_method_text'] || "Please select securaze standard"}</InputLabel>
                                            <Select
                                                name="WipeMethod"
                                                label={translations['wipe_method'] || 'Erasure method'}
                                                margin={"dense"}
                                                value={values.WipeMethod}
                                                onChange={handleChange}
                                                error={!!(errors.WipeMethod && touched.WipeMethod)}
                                            >
                                                {(methods ? [...methods] : []).map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.displayValue}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl className={classes.formControl} margin={"dense"}>
                                            <InputLabel shrink>{translations['please_select_securaze_solution'] || "Please select securaze solution"}</InputLabel>
                                            <Select
                                                name="SecurazeSolution"
                                                label={translations['securaze_solution'] || 'Securaze solution'}
                                                margin={"dense"}
                                                value={values.SecurazeSolution}
                                                onChange={handleChange}
                                                error={!!(errors.SecurazeSolution && touched.SecurazeSolution)}
                                            >
                                                {(securazeSolutions ? [...securazeSolutions] : []).map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.displayValue}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl className={classes.formControl} margin={"dense"}>
                                            <InputLabel shrink>{translations['username_text'] || "Please select user who erased storage"}</InputLabel>
                                            <Select
                                                name="DataWipeEmployeeID"
                                                label={translations['username'] || 'Username'}
                                                margin={"dense"}
                                                value={values.DataWipeEmployeeID === null || values.DataWipeEmployeeID === undefined ? '' : values.DataWipeEmployeeID}
                                                onChange={(event) => setFieldValue('DataWipeEmployeeID', event.target.value === '' ? null : event.target.value)}
                                                error={!!(errors.DataWipeEmployeeID && touched.DataWipeEmployeeID)}
                                            >
                                                <MenuItem value={''}>
                                                    <em>{translations['none'] || 'None'}</em>
                                                </MenuItem>
                                                {(reportInformation ? [...reportInformation] : []).map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.displayValue}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </> : 
                                    <>
                                        <TextField
                                            name="VerificationResult"
                                            value={values.VerificationResult}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={!!(errors.VerificationResult && touched.VerificationResult)}
                                            placeholder={translations['verification_result'] || 'Verification result'}
                                            label={translations['verification_result'] || 'Verification result'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            multiline
                                        />
                                        <TextField
                                            name="VerificationProgress"
                                            value={values.VerificationProgress}
                                            onChange={handleChange}
                                            InputProps={{ inputProps: { min: 0, max: 100 } }}
                                            onBlur={handleBlur}
                                            error={!!(errors.VerificationProgress && touched.VerificationProgress)}
                                            placeholder={translations['verification_progress'] || 'Verification progress'}
                                            label={translations['verification_progress'] || 'Verification progress'}
                                            margin={'dense'}
                                            fullWidth={true}
                                            type='number'
                                        />
                                        <FormHelperText>{translations['verification_started_text'] || 'Please select verification started date'}</FormHelperText>
                                        <DateTimePicker
                                            clearable
                                            name={'VerificationStarted'}
                                            onBlur={handleBlur}
                                            error={!!(errors.VerificationStarted && touched.VerificationStarted)}
                                            onChange={(date) =>
                                                setFieldValue("VerificationStarted", date)
                                            }
                                            ampm={false}
                                            maxDate={values.VerificationFinished ? values.VerificationFinished : ''}
                                            value={values.VerificationStarted !== "" ? values.VerificationStarted : null}
                                            margin={"dense"}
                                            onAccept={date => {
                                                setFieldValue("VerificationStarted", date)
                                            }}
                                            fullWidth={true}
                                            format={`${getDateFormat()} HH:mm:ss`}
                                        />
                                        <FormHelperText>{translations['verification_finished_text'] || 'Please select verification finished date'}</FormHelperText>
                                        <DateTimePicker
                                            clearable
                                            name={'VerificationFinished'}
                                            onBlur={handleBlur}
                                            minDate={values.VerificationStarted ? values.VerificationStarted : moment()}
                                            ampm={false}
                                            error={!!(errors.VerificationFinished && touched.VerificationFinished)}
                                            onChange={(date) => {
                                                console.log('date inside onChange', date)
                                                setFieldValue("VerificationFinished", date)
                                            }
                                            }
                                            value={values.VerificationFinished !== "" ? values.VerificationFinished : null}
                                            margin={"dense"}
                                            onAccept={date => {
                                                setFieldValue("VerificationFinished", date)
                                            }}
                                            fullWidth={true}
                                            format={`${getDateFormat()} HH:mm:ss`}
                                        />
                                        <TextField
                                            name="VerificationDuration"
                                            value={calculateDate('VerificationStarted', 'VerificationFinished')}
                                            onChange={handleChange}
                                            multiline
                                            disabled={true}
                                            onBlur={handleBlur}
                                            error={!!(errors.VerificationDuration && touched.VerificationDuration)}
                                            placeholder={translations['duration'] || 'Duration'}
                                            label={translations['duration'] || 'Duration'}
                                            margin={'dense'}
                                            fullWidth={true}
                                        />
                                        <FormControl className={classes.formControl} margin={"dense"}>
                                            <InputLabel shrink>{translations['verification_status_text'] || "Please select verification status"}</InputLabel>
                                            <Select
                                                name="VerificationStatus"
                                                label={translations['verification_status'] || 'Verification status'}
                                                margin={"dense"}
                                                value={values.VerificationStatus}
                                                onChange={handleChange}
                                                error={!!(errors.VerificationStatus && touched.VerificationStatus)}
                                            >
                                                {(verificationStatuses ? [...verificationStatuses] : []).map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.displayValue}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl className={classes.formControl} margin={"dense"}>
                                            <InputLabel shrink>{translations['verification_method_text'] || "Please select verification standard"}</InputLabel>
                                            <Select
                                                name="VerificationMethod"
                                                label={translations['verification_method'] || 'Verification method'}
                                                margin={"dense"}
                                                value={values.VerificationMethod}
                                                onChange={handleChange}
                                                error={!!(errors.VerificationMethod && touched.VerificationMethod)}
                                            >
                                                {(methods ? [...methods] : []).map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.displayValue}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl className={classes.formControl} margin={"dense"}>
                                            <InputLabel shrink>{translations['verification_username_text'] || "Please select user who verified storage"}</InputLabel>
                                            <Select
                                                name="DataVerificationEmployeeID"
                                                label={translations['username'] || 'Username'}
                                                margin={"dense"}
                                                value={values.DataVerificationEmployeeID === null || values.DataVerificationEmployeeID === undefined ? '' : values.DataVerificationEmployeeID}
                                                onChange={(event) => setFieldValue('DataVerificationEmployeeID', event.target.value === '' ? null : event.target.value)}
                                                error={!!(errors.DataVerificationEmployeeID && touched.DataVerificationEmployeeID)}
                                            >
                                                <MenuItem value={''}>
                                                    <em>{translations['none'] || 'None'}</em>
                                                </MenuItem>
                                                {(reportInformation ? [...reportInformation] : []).map(option => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.displayValue}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </>}

                            <div style={{ textAlign: 'right' }}>
                                {formSettings.ButtonBack ? (
                                    <Button
                                        style={{ width: '120px', marginTop: 10, marginRight: 10, display: activeStep === 0 ? 'none' : null }}
                                        variant='contained'
                                        disabled={activeStep === 0}
                                        color={formSettings.ButtonBack.Color}
                                        onClick={formSettings.ButtonBack.handleBack}
                                    >
                                        {formSettings.ButtonBack.Title}
                                    </Button>
                                ) : null}
                                {formSettings.ButtonNext ? (
                                    <Button
                                        style={{ width: '120px', marginTop: 10, marginRight: 10, display: activeStep === 3 ? 'none' : null }}
                                        variant='contained'
                                        disabled={edit ? false : loading || !(isValid && dirty) || isSubmitting}
                                        color={formSettings.ButtonNext.Color}
                                        onClick={formSettings.ButtonNext.handleNext}
                                    >
                                        {formSettings.ButtonNext.Title}
                                    </Button>
                                ) : null}
                                {formSettings.ButtonSubmit && activeStep !== 0 && activeStep !== 1 && activeStep !== 2 ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        disabled={loading || !(isValid && dirty) || isSubmitting || !checkForWipeDate || !checkForVerificationDate}
                                    >
                                        {!create ? (
                                            loading ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                    formSettings.ButtonSubmit.Title
                                                )
                                        ) : isSubmitting ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                                    formSettings.ButtonSubmit.Title
                                                )}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    </div>
                );
            }}
        </Formik>
    );
};

export default withStyles(styles, { withTheme: true })(StorageForm);
